import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { bt, btp } from '../../i18n';
import type { GradientField } from '@aquilixshop/ui-react';
import { FieldRow } from '../FieldRow';
import { HsvColorPicker } from './HsvColorPicker';
import { clamp, hexToRgb, rgbToHex } from './colorUtils';

/**
 * Gradient editor (Shopify `color_background` look): a compact preview bar that opens a POPOVER with the
 * gradient bar (selectable + draggable stops) + the embedded HSV picker for the selected stop + add/remove
 * + an angle. Parses/serializes a `linear-gradient(<angle>deg, <color> <pos>%, …)` STRING (the stored
 * value). Stop colors are normalised to hex so the HSV picker can edit them.
 */
interface Stop {
  color: string;
  pos: number;
}
interface Grad {
  angle: number;
  stops: Stop[];
}
const DEFAULT_GRAD: Grad = {
  angle: 90,
  stops: [
    { color: '#' + '7c3aed', pos: 0 },
    { color: '#' + '2bd4ff', pos: 100 },
  ],
};

function splitTopLevel(s: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let cur = '';
  for (const ch of s) {
    if (ch === '(') depth++;
    if (ch === ')') depth--;
    if (ch === ',' && depth === 0) {
      out.push(cur);
      cur = '';
    } else cur += ch;
  }
  if (cur.trim()) out.push(cur);
  return out;
}
function toHex(c: string): string {
  const s = c.trim();
  if (s.startsWith('#')) {
    const rgb = hexToRgb(s);
    return rgb ? rgbToHex(rgb.r, rgb.g, rgb.b) : '#' + '888888';
  }
  const m = /rgba?\(([^)]+)\)/i.exec(s);
  if (m) {
    const [r, g, b] = (m[1] ?? '').split(',').map((x) => parseFloat(x));
    return rgbToHex(r ?? 0, g ?? 0, b ?? 0);
  }
  return '#' + '888888';
}
function parseGradient(css: string | undefined): Grad {
  const m = /linear-gradient\((.*)\)/is.exec((css ?? '').trim());
  if (!m) return DEFAULT_GRAD;
  const parts = splitTopLevel(m[1] ?? '').map((p) => p.trim());
  let angle = 90;
  let idx = 0;
  const first = parts[0] ?? '';
  if (/deg|to\s/i.test(first) && !/rgb|#/i.test(first)) {
    const a = /(-?\d+(?:\.\d+)?)deg/i.exec(first);
    if (a) angle = parseFloat(a[1] ?? '90');
    else if (/to\s+right/i.test(first)) angle = 90;
    else if (/to\s+left/i.test(first)) angle = 270;
    else if (/to\s+top/i.test(first)) angle = 0;
    else if (/to\s+bottom/i.test(first)) angle = 180;
    idx = 1;
  }
  const raw = parts.slice(idx);
  const stops: Stop[] = raw.map((p, i) => {
    const posM = /(\d+(?:\.\d+)?)%\s*$/.exec(p);
    const pos = posM
      ? parseFloat(posM[1] ?? '0')
      : raw.length > 1
        ? Math.round((i / (raw.length - 1)) * 100)
        : 0;
    return { color: toHex(p.replace(/\s*\d+(?:\.\d+)?%\s*$/, '')), pos };
  });
  return stops.length >= 2 ? { angle, stops } : DEFAULT_GRAD;
}
function serialize(g: Grad): string {
  // SORTED. CSS raises any stop that sits below its predecessor up to that predecessor's position, so a
  // handle dragged past its neighbour stopped blending and painted a hard edge instead — with the state
  // still showing the position the seller chose.
  const body = [...g.stops]
    .sort((a, b) => a.pos - b.pos)
    .map((s) => `${s.color} ${Math.round(s.pos)}%`)
    .join(', ');
  return `linear-gradient(${Math.round(g.angle)}deg, ${body})`;
}

/** The colour half-way between two stops — what a new stop dropped between them should start as. */
function mixHex(a: string, b: string): string {
  const x = hexToRgb(a);
  const y = hexToRgb(b);
  if (!x || !y) return a;
  return rgbToHex((x.r + y.r) / 2, (x.g + y.g) / 2, (x.b + y.b) / 2);
}

function startDrag(handler: (e: PointerEvent) => void, e: React.PointerEvent) {
  handler(e.nativeEvent);
  const move = (ev: PointerEvent) => handler(ev);
  const up = () => {
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
  };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
}

export function GradientControl({
  field,
  value,
  onChange,
}: {
  field: GradientField;
  value: string | undefined;
  onChange: (next: string | undefined) => void;
}) {
  // An `auto` field keeps its UNSET state visible (mirrors ColorControl): the trigger wears the neutral
  // hatch and the popover offers "None", which CLEARS the setting instead of writing a gradient string.
  // Without it a gradient could be turned on but never off — the seller's only way back was undo.
  const off = field.auto === true && !value;
  const css =
    typeof value === 'string' && value ? value : (field.default ?? serialize(DEFAULT_GRAD));
  const [g, setG] = useState<Grad>(() => parseGradient(css));
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => setG(parseGradient(css)), [css]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const selIdx = Math.min(sel, g.stops.length - 1);
  const selected = g.stops[selIdx] ?? { color: '#' + '888888', pos: 0 };

  const commit = (next: Grad) => {
    setG(next);
    onChange(serialize(next));
  };
  const setStopColor = (i: number, hex: string) =>
    commit({ ...g, stops: g.stops.map((s, j) => (j === i ? { ...s, color: hex } : s)) });
  const setStopPos = (i: number, pos: number) =>
    commit({
      ...g,
      stops: g.stops.map((s, j) => (j === i ? { ...s, pos: clamp(pos, 0, 100) } : s)),
    });
  /**
   * Add a colour WHERE THERE IS ROOM FOR IT.
   *
   * This used to append `{ color: <the last stop's colour>, pos: 100 }` — a duplicate of the last stop, on
   * top of the last stop. Clicking it did nothing visible, so the seller clicked again, and again: four
   * handles piled at the end and a bar that had barely changed (founder, 2026-09-20: "how on earth does
   * this work, I want three colors here"). A third colour belongs in the middle, and it starts as the
   * blend already painted there, so the bar looks identical the instant it appears and then follows the
   * picker.
   */
  const addStop = () => {
    const sorted = [...g.stops].sort((a, b) => a.pos - b.pos);
    let at = 0;
    let widest = -1;
    for (let i = 0; i < sorted.length - 1; i++) {
      const gap = (sorted[i + 1]?.pos ?? 0) - (sorted[i]?.pos ?? 0);
      if (gap > widest) {
        widest = gap;
        at = i;
      }
    }
    const left = sorted[at] ?? { color: '#' + '888888', pos: 0 };
    const right = sorted[at + 1] ?? { color: '#' + '888888', pos: 100 };
    const fresh = { color: mixHex(left.color, right.color), pos: (left.pos + right.pos) / 2 };
    const next = [...sorted.slice(0, at + 1), fresh, ...sorted.slice(at + 1)];
    commit({ ...g, stops: next });
    setSel(at + 1);
  };
  const removeStop = (i: number) => {
    if (g.stops.length <= 2) return;
    commit({ ...g, stops: g.stops.filter((_, j) => j !== i) });
    setSel(0);
  };
  const dragStop = (i: number) => (e: React.PointerEvent) => {
    setSel(i);
    startDrag((ev) => {
      const r = barRef.current?.getBoundingClientRect();
      if (!r) return;
      setStopPos(i, Math.round(clamp((ev.clientX - r.left) / r.width, 0, 1) * 100));
    }, e);
  };

  return (
    <FieldRow id={field.id} label={field.label} info={field.info} labelAs="span" layout="stack">
      <div className="bld-grad" ref={ref}>
        <button
          type="button"
          className={`bld-grad__trigger${off ? ' bld-grad__trigger--auto' : ''}`}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={btp('chrome.grad.editor_aria', '{name} editor', { name: field.label })}
          style={off ? undefined : ({ background: serialize(g) } as CSSProperties)}
          onClick={() => setOpen((v) => !v)}
        />
        {off && <span className="bld-grad__offlabel">{bt('chrome.grad.none', 'None')}</span>}

        {open && (
          <div
            className="bld-grad__pop"
            role="dialog"
            aria-label={btp('chrome.grad.editor_aria', '{name} editor', { name: field.label })}
          >
            <div
              className="bld-grad__bar"
              ref={barRef}
              style={{ background: serialize({ ...g, angle: 90 }) } as CSSProperties}
            >
              {g.stops.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  className={`bld-grad__handle${i === selIdx ? ' is-selected' : ''}`}
                  aria-label={btp('chrome.grad.stop_aria', 'Color stop {n}', { n: i + 1 })}
                  style={{ left: `${s.pos}%`, background: s.color } as CSSProperties}
                  onPointerDown={dragStop(i)}
                  onClick={() => setSel(i)}
                />
              ))}
            </div>

            {/* The stop LIST. The handles on the bar are 12px and they overlap — a seller who wants three
                colours cannot see how many she has, let alone pick the middle one. One numbered chip per
                colour, in order, says both. */}
            <div
              className="bld-grad__stops"
              role="radiogroup"
              aria-label={btp('chrome.grad.stops_aria', '{name} colors', { name: field.label })}
            >
              {g.stops.map((stop, i) => (
                <button
                  key={i}
                  type="button"
                  role="radio"
                  aria-checked={i === selIdx}
                  className={`bld-grad__stop${i === selIdx ? ' is-selected' : ''}`}
                  aria-label={btp('chrome.grad.stop_aria', 'Color stop {n}', { n: i + 1 })}
                  onClick={() => setSel(i)}
                >
                  <span className="bld-grad__stopchip" style={{ background: stop.color }} />
                  <span className="bld-grad__stophex">{stop.color.toUpperCase()}</span>
                </button>
              ))}
            </div>

            <HsvColorPicker
              value={selected.color}
              onChange={(hex) => setStopColor(selIdx, hex)}
              hexLabel={btp('chrome.grad.stop_hex_aria', '{name} stop color', {
                name: field.label,
              })}
            />

            <div className="bld-grad__controls">
              <label className="bld-grad__ctl">
                <span>{bt('chrome.grad.position', 'Position')}</span>
                <input
                  className="bld-input bld-grad__num"
                  type="number"
                  min={0}
                  max={100}
                  value={Math.round(selected.pos)}
                  aria-label={btp('chrome.grad.stop_pos_aria', '{name} stop position', {
                    name: field.label,
                  })}
                  onChange={(e) => setStopPos(selIdx, Number(e.target.value))}
                />
                <span className="bld-grad__unit">%</span>
              </label>
              {g.stops.length > 2 && (
                <button
                  type="button"
                  className="bld-grad__remove"
                  onClick={() => removeStop(selIdx)}
                >
                  {bt('chrome.grad.remove', 'Remove')}
                </button>
              )}
              <button type="button" className="bld-grad__add" onClick={addStop}>
                {bt('chrome.grad.add', '+ Add color')}
              </button>
            </div>

            <label className="bld-grad__ctl">
              <span>{bt('chrome.grad.angle', 'Angle')}</span>
              <input
                className="bld-input bld-grad__num"
                type="number"
                min={0}
                max={360}
                value={Math.round(g.angle)}
                aria-label={btp('chrome.grad.angle_aria', '{name} angle', { name: field.label })}
                onChange={(e) => commit({ ...g, angle: clamp(Number(e.target.value), 0, 360) })}
              />
              <span className="bld-grad__unit">°</span>
            </label>

            {/* The way OUT. An `auto` gradient is an override the seller must be able to lift; clearing the
                setting (not writing a neutral gradient) is what puts the element back on the section's own
                colour. Only offered once there is something to clear. */}
            {field.auto === true && !off && (
              <button
                type="button"
                className="bld-grad__none"
                onClick={() => {
                  onChange(undefined);
                  setOpen(false);
                }}
              >
                {bt('chrome.grad.none', 'None')}
              </button>
            )}
          </div>
        )}
      </div>
    </FieldRow>
  );
}
